import { useState, useCallback } from 'react';

export interface RecipeFormState {
  recipeName: string;
  aliases: string;
  description: string;
  servings: number | '';
  nutritionServings: number;
  dietLabels: string[];
  allergies: string[];
  intoleranceTags: string[];
  cuisineTypes: string[];
  mealTypes: string[];
  dishTypes: string[];
  cookingMethod: string;
  shareWithOthers: boolean;
  instructions: string;
  cookingTips: string;
  prepTimeMinutes: number | '';
  cookTimeMinutes: number | '';
  totalTimeMinutes: number;
  defaultServingGrams: number | '';
}

export const useRecipeForm = (initialState?: Partial<RecipeFormState>) => {
  const [recipeName, setRecipeName] = useState(initialState?.recipeName || '');
  const [aliases, setAliases] = useState(initialState?.aliases || '');
  const [description, setDescription] = useState(initialState?.description || '');
  const [servings, setServings] = useState<number | ''>(initialState?.servings ?? 1);
  const [nutritionServings, setNutritionServings] = useState(initialState?.nutritionServings || 1);
  const [dietLabels, setDietLabels] = useState<string[]>(initialState?.dietLabels || []);
  const [allergies, setAllergies] = useState<string[]>(initialState?.allergies || []);
  const [intoleranceTags, setIntoleranceTags] = useState<string[]>(initialState?.intoleranceTags || []);
  const [cuisineTypes, setCuisineTypes] = useState<string[]>(initialState?.cuisineTypes || []);
  const [mealTypes, setMealTypes] = useState<string[]>(initialState?.mealTypes || []);
  const [dishTypes, setDishTypes] = useState<string[]>(initialState?.dishTypes || []);
  const [cookingMethod, setCookingMethod] = useState(initialState?.cookingMethod || '');
  const [shareWithOthers, setShareWithOthers] = useState(initialState?.shareWithOthers || false);
  const [instructions, setInstructions] = useState(initialState?.instructions || '');
  const [cookingTips, setCookingTips] = useState(initialState?.cookingTips || '');
  const [prepTimeMinutes, setPrepTimeMinutes] = useState<number | ''>(initialState?.prepTimeMinutes || 0);
  const [cookTimeMinutes, setCookTimeMinutes] = useState<number | ''>(initialState?.cookTimeMinutes || 0);
  const [totalTimeMinutes, setTotalTimeMinutes] = useState(initialState?.totalTimeMinutes || 0);
  const [defaultServingGrams, setDefaultServingGrams] = useState<number | ''>(initialState?.defaultServingGrams ?? '');

  const updatePrepTime = useCallback((value: number | '') => {
    setPrepTimeMinutes(value);
  }, []);

  const updateCookTime = useCallback((value: number | '') => {
    setCookTimeMinutes(value);
  }, []);

  const updateTotalTime = useCallback((prep: number, cook: number) => {
    setTotalTimeMinutes(prep + cook);
  }, []);

  return {
    // State
    recipeName,
    aliases,
    description,
    servings,
    nutritionServings,
    dietLabels,
    allergies,
    intoleranceTags,
    cuisineTypes,
    mealTypes,
    dishTypes,
    cookingMethod,
    shareWithOthers,
    instructions,
    cookingTips,
    prepTimeMinutes,
    cookTimeMinutes,
    totalTimeMinutes,
    defaultServingGrams,
    // Setters
    setRecipeName,
    setAliases,
    setDescription,
    setServings,
    setNutritionServings,
    setDietLabels,
    setAllergies,
    setIntoleranceTags,
    setCuisineTypes,
    setMealTypes,
    setDishTypes,
    setCookingMethod,
    setShareWithOthers,
    setInstructions,
    setCookingTips,
    updatePrepTime,
    updateCookTime,
    updateTotalTime,
    setDefaultServingGrams,
  };
};
